import { LineString, Point } from "geojson";
import {
  AdjacentFeatureDistances,
  DistanceTracking,
  SafetyConcern,
} from "./models";
import { processLineStringAdjacentFeature } from "./processLineStringAdjacentFeature";
import { processPolygonAdjacentFeature } from "./processPolygonAdjacentFeature";
import { isFeatureWithinPolygon } from "../map-utils";
import { typedEntries } from "@keita/lanepatrol-sdk";
import turf from "../../../shared/utils/turf";
import { CustomSource } from "../../models/CustomSource";
import { importHlaUtils } from "../../sources/import-hla/utils";
import { HlaWayBuilder } from "../../sources/import-hla/models/HlaWayBuilder";

const breakPointsFractions = [0.25, 0.5, 0.75];
const takeNthLowestValue = 2;

/**
 * This is a bit confusing, so I better leave a record of what my thinking:
 * There are 3 main types of geometries we're searching for:
 *    Point > bus stops, parking
 *    LineString > roads, paths, rails
 *    Polygon > building
 *
 * For points, we make 1 query based on a buffer around the main road.
 *    Since we need a bbox for the query, we make a bbox around the buffer
 *    and then we need to filter which features are inside the original buffer.
 *    After that, we calculate the distance from each point to the main road
 *    and find out which side of the road they are on.
 *
 * For lines and polygons, the idea is that we only want to consider features
 *    that appear frequently along the main road. For this, we break the main road
 *    using breakpoint at fixed parts of the road (25%, 50%, 75%). We may want to
 *    add more breakpoints in the future. Since we have 3 points, we decide that
 *    a feature needs to be detected in at least 2 of the breakpoints in order to
 *    be taken into account. We will get distance values for each breakpoint and
 *    then take the second lowest distance (i believe this gets us a better
 *    chance of avoiding "unlucky intersections")
 */
export function findAndRegisterAdjacentFeatures(
  featureId: number,
  lineString: LineString,
  map: mapboxgl.Map,
  builder: HlaWayBuilder,
) {
  const distances = new AdjacentFeatureDistances();

  findAdjacentPointFeatures(distances, lineString, map);

  findLineAndPolygonAdjacentFeatures(distances, featureId, lineString, map);

  console.log("Distances to each adjacent feature type:", distances);

  applyAdjacentFeatures(builder, distances.data);
}

function findAdjacentPointFeatures(
  distances: AdjacentFeatureDistances,
  lineString: LineString,
  map: mapboxgl.Map,
) {
  // Create a buffer polygon around the road line
  const bufferPolygon = turf.buffer(lineString, 8, {
    units: "meters",
  })!;

  // Get the bounding box of the buffer to query the map efficiently
  const bbox = importHlaUtils.polygonToBbox(bufferPolygon, map);
  const candidates = map.queryRenderedFeatures(bbox, {
    layers: [CustomSource.BusStop, CustomSource.Parking],
  });

  candidates.forEach((feature) => {
    const isWithinBuffer = isFeatureWithinPolygon(feature, bufferPolygon);
    if (!isWithinBuffer) return;

    if (feature.layer?.id === CustomSource.Parking) {
      // Add assumed margin since we want to consider the edge of the parking space and not the center
      const averageParkingWidth = 2.8;
      const averageLaneWidth = 2.8;
      const assumedMargin = averageParkingWidth / 2 + averageLaneWidth / 2;
      const relativePosition = importHlaUtils.determineSideAndDistance(
        feature.geometry as Point,
        lineString,
      );

      if (relativePosition) {
        distances.registerFeature(
          SafetyConcern.Parking,
          relativePosition.side,
          Math.max(0, relativePosition.distance - assumedMargin),
        );
      }
    } else if (feature.layer?.id === CustomSource.BusStop) {
      const averageLaneWidth = 2.8;
      const assumedMargin = averageLaneWidth / 2;
      const relativePosition = importHlaUtils.determineSideAndDistance(
        feature.geometry as Point,
        lineString,
      );
      if (relativePosition) {
        distances.registerFeature(
          SafetyConcern.BusStop,
          relativePosition.side,
          Math.max(0, relativePosition.distance - assumedMargin),
        );
      }
    }
  });
}

function findLineAndPolygonAdjacentFeatures(
  distances: AdjacentFeatureDistances,
  featureId: number,
  mainLine: LineString,
  map: mapboxgl.Map,
) {
  const mainFeature = turf.lineString(mainLine.coordinates);
  const totalLength = turf.length(mainFeature, { units: "meters" });

  const concernsAtEachBreakpoint: Partial<
    Record<SafetyConcern, Record<"right" | "left", number>[]>
  > = {
    Path: [],
    Building: [],
    Street: [],
    Rail: [],
  };

  breakPointsFractions.forEach((fraction) => {
    const breakPoint = turf.along(mainLine, totalLength * fraction, {
      units: "meters",
    });
    const breakPointBuffer = turf.buffer(breakPoint, 5, {
      units: "meters",
    })!;

    const bbox = importHlaUtils.polygonToBbox(breakPointBuffer, map);

    const candidates = map.queryRenderedFeatures(bbox, {
      layers: ["road-rail", "road-street", "road-path", "building"],
    });

    candidates.forEach((feature) => {
      // Exclude the feature we're currently using as a main way
      if (featureId === feature.id) return;

      const isWithinBuffer = isFeatureWithinPolygon(feature, breakPointBuffer);
      if (!isWithinBuffer) return;

      if (feature.layer?.id === "building") {
        concernsAtEachBreakpoint.Building?.push(
          processPolygonAdjacentFeature(mainLine, feature),
        );
      } else if (feature.layer?.id === "road-path") {
        concernsAtEachBreakpoint.Path?.push(
          processLineStringAdjacentFeature(
            mainLine,
            breakPoint.geometry,
            feature,
          ),
        );
      } else if (feature.layer?.id === "road-street") {
        concernsAtEachBreakpoint.Street?.push(
          processLineStringAdjacentFeature(
            mainLine,
            breakPoint.geometry,
            feature,
          ),
        );
      } else if (feature.layer?.id === "road-rail") {
        concernsAtEachBreakpoint.Rail?.push(
          processLineStringAdjacentFeature(
            mainLine,
            breakPoint.geometry,
            feature,
          ),
        );
      }
    });
  });

  registerDistances(distances, concernsAtEachBreakpoint);
}

// TODO this can probably be simplified
function registerDistances(
  distances: AdjacentFeatureDistances,
  concernsAtEachBreakpoint: Partial<
    Record<SafetyConcern, Record<"right" | "left", number>[]>
  >,
) {
  for (const [concern, breakpoints] of typedEntries<
    SafetyConcern,
    Record<"left" | "right", number>[]
  >(concernsAtEachBreakpoint)) {
    const leftDistances = breakpoints.map((b) => b.left).toSorted();
    if (
      leftDistances[takeNthLowestValue] &&
      leftDistances[takeNthLowestValue] < 10
    ) {
      distances.registerFeature(
        concern,
        "left",
        leftDistances[takeNthLowestValue],
      );
    }

    const rightDistances = breakpoints.map((b) => b.right).toSorted();
    if (
      rightDistances[takeNthLowestValue] &&
      rightDistances[takeNthLowestValue] < 10
    ) {
      distances.registerFeature(
        concern,
        "right",
        rightDistances[takeNthLowestValue],
      );
    }
  }
}

function applyAdjacentFeatures(b: HlaWayBuilder, distances: DistanceTracking) {
  if (distances.BusStop.left || distances.BusStop.right) {
    b.busStop = true;
  }

  if (distances.Parking.left) {
    b.left.parking = "parallel";
  }
  if (distances.Parking.right) {
    b.right.parking = "parallel";
  }

  if (distances.Building.left) {
    b.left.building = true;
  }
  if (distances.Building.right) {
    b.right.building = true;
  }

  if (distances.Building.left) {
    b.left.building = true;
  }
  if (distances.Building.right) {
    b.right.building = true;
  }

  if (distances.Path.left) {
    b.left.sidewalk = true;
  }
  if (distances.Path.right) {
    b.right.sidewalk = true;
  }

  if (distances.Rail[b.frontSide]) {
    b.tramOrTrainRailsForward = true;
  }
  if (distances.Rail[b.backSide]) {
    b.tramOrTrainRailsBackward = true;
  }
}
